// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from '@/integrations/supabase/client';
import { Plus, Star, Phone, Mail, Calendar, Users, Heart, Award } from 'lucide-react';
import { toast } from 'sonner';
import { getSessionUser } from '@/lib/authSession';

interface CoachRelationship {
  id: string;
  coach_name: string;
  relationship_strength: string;
  communication_preferences: string[];
  best_contact_times: string;
  personal_notes: string;
  referral_potential: number;
  years_coaching: number;
  coaching_certifications: string[];
  previous_events_attended: string[];
  contact_frequency: string;
  last_interaction_date: string;
  next_scheduled_contact: string;
  team_prospects?: {
    team_name: string;
    sport: string;
    school_organization: string;
  };
}

const CoachRelationshipTracker: React.FC = () => {
  const [relationships, setRelationships] = useState<CoachRelationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNewRelationshipOpen, setIsNewRelationshipOpen] = useState(false);
  const [newRelationship, setNewRelationship] = useState({
    team_id: '',
    coach_name: '',
    relationship_strength: 'cold',
    best_contact_times: '',
    personal_notes: '',
    referral_potential: '5',
    years_coaching: '',
    contact_frequency: 'monthly'
  });
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get current user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await getSessionUser()).data.user?.id)
        .single();

      if (!profile) return;

      // Fetch coach relationships with team info
      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from('coach_relationships')
        .select(`
          *,
          team_prospects (
            team_name,
            sport,
            school_organization
          )
        `)
        .eq('sales_rep_id', profile.id)
        .order('relationship_strength', { ascending: false });

      if (relationshipsError) throw relationshipsError;

      // Fetch available teams for the dropdown
      const { data: teamsData, error: teamsError } = await supabase
        .from('team_prospects')
        .select('id, team_name, coach_name, sport, school_organization')
        .eq('sales_rep_id', profile.id);

      if (teamsError) throw teamsError;

      setRelationships(relationshipsData || []);
      setAvailableTeams(teamsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load coach relationships');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRelationship = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await getSessionUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const relationshipData = {
        ...newRelationship,
        sales_rep_id: profile.id,
        referral_potential: parseInt(newRelationship.referral_potential),
        years_coaching: newRelationship.years_coaching ? parseInt(newRelationship.years_coaching) : null
      };

      const { error } = await supabase
        .from('coach_relationships')
        .insert([relationshipData]);

      if (error) throw error;

      toast.success('Coach relationship added successfully');
      setIsNewRelationshipOpen(false);
      setNewRelationship({
        team_id: '',
        coach_name: '',
        relationship_strength: 'cold',
        best_contact_times: '',
        personal_notes: '',
        referral_potential: '5',
        years_coaching: '',
        contact_frequency: 'monthly'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating relationship:', error);
      toast.error('Failed to add coach relationship');
    }
  };

  const getRelationshipColor = (strength: string) => {
    switch (strength) {
      case 'champion': return 'text-green-600';
      case 'strong': return 'text-blue-600';
      case 'warm': return 'text-yellow-600';
      case 'cold': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getRelationshipIcon = (strength: string) => {
    switch (strength) {
      case 'champion': return <Award className="h-4 w-4" />;
      case 'strong': return <Heart className="h-4 w-4" />;
      case 'warm': return <Star className="h-4 w-4" />;
      case 'cold': return <Users className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  const getReferralPotentialStars = (potential: number) => {
    return Array.from({ length: 10 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < potential ? 'text-yellow-500 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with New Relationship Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Coach Relationships</h2>
          <p className="text-muted-foreground">
            Build and maintain relationships with team coaches
          </p>
        </div>
        <Dialog open={isNewRelationshipOpen} onOpenChange={setIsNewRelationshipOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Coach
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Coach Relationship</DialogTitle>
              <DialogDescription>
                Track and manage your relationship with a team coach
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="team_id">Team</Label>
                <Select onValueChange={(value) => {
                  setNewRelationship(prev => ({ ...prev, team_id: value }));
                  const team = availableTeams.find(t => t.id === value);
                  if (team) {
                    setNewRelationship(prev => ({ ...prev, coach_name: team.coach_name }));
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTeams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.team_name} - {team.coach_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="coach_name">Coach Name</Label>
                <Input
                  id="coach_name"
                  value={newRelationship.coach_name}
                  onChange={(e) => setNewRelationship(prev => ({ ...prev, coach_name: e.target.value }))}
                  placeholder="Coach full name"
                />
              </div>
              <div>
                <Label htmlFor="relationship_strength">Relationship Strength</Label>
                <Select onValueChange={(value) => setNewRelationship(prev => ({ ...prev, relationship_strength: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select strength" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cold">Cold</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="strong">Strong</SelectItem>
                    <SelectItem value="champion">Champion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="contact_frequency">Contact Frequency</Label>
                <Select onValueChange={(value) => setNewRelationship(prev => ({ ...prev, contact_frequency: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi_weekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="referral_potential">Referral Potential (1-10)</Label>
                <Input
                  id="referral_potential"
                  type="number"
                  min="1"
                  max="10"
                  value={newRelationship.referral_potential}
                  onChange={(e) => setNewRelationship(prev => ({ ...prev, referral_potential: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="years_coaching">Years Coaching</Label>
                <Input
                  id="years_coaching"
                  type="number"
                  value={newRelationship.years_coaching}
                  onChange={(e) => setNewRelationship(prev => ({ ...prev, years_coaching: e.target.value }))}
                  placeholder="Years of experience"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="best_contact_times">Best Contact Times</Label>
                <Input
                  id="best_contact_times"
                  value={newRelationship.best_contact_times}
                  onChange={(e) => setNewRelationship(prev => ({ ...prev, best_contact_times: e.target.value }))}
                  placeholder="e.g., Weekday evenings after 6pm"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="personal_notes">Personal Notes</Label>
                <Textarea
                  id="personal_notes"
                  value={newRelationship.personal_notes}
                  onChange={(e) => setNewRelationship(prev => ({ ...prev, personal_notes: e.target.value }))}
                  placeholder="Personal information, interests, preferences..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsNewRelationshipOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRelationship}>
                Add Coach
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Relationship Strength Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['champion', 'strong', 'warm', 'cold'].map((strength) => {
          const count = relationships.filter(r => r.relationship_strength === strength).length;
          return (
            <Card key={strength}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground capitalize">
                      {strength} Relationships
                    </p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <div className={getRelationshipColor(strength)}>
                    {getRelationshipIcon(strength)}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Coach Relationships Table */}
      <Card>
        <CardHeader>
          <CardTitle>Coach Relationships</CardTitle>
          <CardDescription>
            Manage your relationships with team coaches
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Coach</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Relationship</TableHead>
                <TableHead>Referral Potential</TableHead>
                <TableHead>Contact Frequency</TableHead>
                <TableHead>Last Contact</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {relationships.map((relationship) => (
                <TableRow key={relationship.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{relationship.coach_name}</div>
                      {relationship.years_coaching && (
                        <div className="text-sm text-muted-foreground">
                          {relationship.years_coaching} years coaching
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{relationship.team_prospects?.team_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {relationship.team_prospects?.sport} • {relationship.team_prospects?.school_organization}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={getRelationshipColor(relationship.relationship_strength)}>
                        {getRelationshipIcon(relationship.relationship_strength)}
                      </span>
                      <span className="capitalize">{relationship.relationship_strength}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {getReferralPotentialStars(relationship.referral_potential)}
                      <span className="ml-2 text-sm text-muted-foreground">
                        {relationship.referral_potential}/10
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="capitalize">{relationship.contact_frequency}</TableCell>
                  <TableCell>
                    {relationship.last_interaction_date ? 
                      new Date(relationship.last_interaction_date).toLocaleDateString() : 
                      'Never'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm">
                        <Phone className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Calendar className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export { CoachRelationshipTracker };